/* eslint-disable camelcase */
/* eslint-disable no-unused-vars */
import React, { useEffect, useState, useMemo, useContext } from 'react';
import {
  AppBar,
  Container,
  Box,
  IconButton,
  Toolbar,
  Typography,
  Menu,
  Button,
  MenuItem,
  useMediaQuery,
} from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import { styled } from '@mui/material/styles';
// router
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import LanguageIcon from '@mui/icons-material/Language';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import NotificationsPopover from '../../components/NotificationsPopover';
// hooks
import useLocales from '../../hooks/useLocales';
import useLang from '../../hooks/useLang';

import createSaveConfirmation from '../../utils/saveConfirmation';

const LangText = styled(Typography)({
  fontSize: '14px',
  lineHeight: '20px',
  width: '100px',
});

const langComparison = {
  zh: 'zh',
  english: 'en',
  cn: 'cn',
};

const Link = styled('a')({
  textDecoration: 'none',
});

let unloading = false;
let timer = null;
const beforeunload = (e) => {
  unloading = true;
};

const MainHeader = () => {
  const { info } = useSelector((state) => state.main);
  const { access_token } = useSelector((state) => state.user);
  const { t } = useTranslation('main');
  const navigate = useNavigate();
  const [anchorElUser, setAnchorElUser] = React.useState(null);
  const { allLang, currentLang, onChangeLang } = useLocales();
  const { language } = useLang();
  const { pathname } = useLocation();

  // 手机端适配
  const isWebpage = useMediaQuery('(min-width:600px)');
  const isMDdown = useMediaQuery('(max-width:900px)');
  const isIPadPro = useMediaQuery('(max-width:1024px)');
  // 常见问题的文件
  const commonProblem =
    info.file_list && info.file_list.find((_item) => _item.type === 'QA');

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const [tempValue, setTempValue] = useState('');
  // 改变语言
  const handleChangeLang = (value) => {
    onChangeLang(value);
    setTempValue(currentLang.value);
    if (currentLang.value !== value) {
      // 更换语言,返回首页
      navigate(`/${langComparison[value]}`);
    }
    setAnchorElUser(null);
  };

  useEffect(() => {
    window.addEventListener('beforeunload', beforeunload);
    return () => {
      window.removeEventListener('beforeunload', beforeunload);
    };
  }, []);

  useEffect(() => {
    timer = setInterval(() => {
      if (unloading) {
        // reset var
        unloading = false;
        // wait a second, to see if the user left or not
        setTimeout(() => {
          onChangeLang(tempValue);
        }, 0);
      }
    }, 400);
    return () => {
      clearInterval(timer);
    };
  }, [tempValue]);

  // language switch
  const languageHandler = (option) => {
    if (pathname === `/${language}` || language === option.rename) {
      handleChangeLang(option.value);
      return;
    }

    createSaveConfirmation({
      callback: () => handleChangeLang(option.value),
      language,
      pathname,
    });
  };

  return (
    <AppBar
      position="static"
      sx={{
        backgroundColor: '#375a7f',
        height: '85px',
        '@media print': {
          display: 'none',
        },
      }}
    >
      <Container maxWidth="95%" sx={{ height: '100%' }}>
        <Toolbar disableGutters sx={{ height: '100%' }}>
          <Typography
            noWrap
            component="div"
            sx={{
              mr: 2,
              display: { xs: 'none', md: 'none', lg: 'flex' },
              boxSizing: 'border-box',
            }}
          >
            <Box
              sx={{ width: '150px', cursor: 'pointer', textAlign: 'center' }}
              onClick={(e) => {
                createSaveConfirmation({
                  callback: () =>
                    navigate(`/${langComparison[currentLang.value]}`),
                  language,
                  pathname,
                });
              }}
            >
              {info.logo ? (
                <img
                  style={{
                    objectFit: 'contain',
                    objectPosition: '50% 50%',
                    width: '150px',
                    height: '85px',
                    display: 'block',
                  }}
                  src={info.logo}
                  alt=""
                />
              ) : (
                <Skeleton variant="rounded" width={150} height={65} />
              )}
            </Box>
          </Typography>
          {/* 手机版的logo */}
          <Typography
            noWrap
            component="div"
            sx={{
              mr: isWebpage ? 2 : 0,
              ...(!isWebpage && { flexGrow: 1 }),
              display: { md: 'flex', lg: 'none' },
              boxSizing: 'border-box',
            }}
          >
            <Box
              sx={{
                width: '150px',
                cursor: 'pointer',
                textAlign: 'center',
              }}
              onClick={(e) => {
                createSaveConfirmation({
                  callback: () =>
                    navigate(`/${langComparison[currentLang.value]}`),
                  language,
                  pathname,
                });
              }}
            >
              {info.logo ? (
                <img
                  style={{
                    objectFit: 'contain',
                    objectPosition: '50% 50%',
                    width: '150px',
                    height: '85px',
                    display: 'block',
                  }}
                  src={info.logo}
                  alt=""
                />
              ) : (
                <Skeleton variant="rounded" width={150} height={85} />
              )}
            </Box>
          </Typography>
          <Box
            sx={{
              flexGrow: 1,
              display: { xs: 'none', sm: 'flex', md: 'flex', lg: 'flex' },
              justifyContent: 'center',
            }}
          >
            <Typography
              variant="h5"
              sx={{ ...(isIPadPro && { fontSize: '1rem' }) }}
              noWrap
              component="div"
            >
              {/* 保良局教育事務部屬下幼稚園 */}
              {info.name ? (
                info.name
              ) : (
                <Skeleton variant="rounded" width={300} height={45} />
              )}
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 0, height: '100%', textDecoration: 'none' }}>
            {access_token && <NotificationsPopover />}
            {commonProblem && (
              <Link
                href={
                  commonProblem?.file ||
                  `https://application.plkkgs.edu.hk/faq/${language}.pdf`
                }
                target="_blank"
                rel="noreferrer"
              >
                {!isMDdown ? (
                  <Button
                    variant="text"
                    startIcon={<ContactSupportIcon />}
                    sx={{ color: 'white', height: '100%' }}
                  >
                    {t('common_problem')}
                  </Button>
                ) : (
                  <IconButton sx={{ color: 'white', height: '100%' }}>
                    <ContactSupportIcon />
                  </IconButton>
                )}
              </Link>
            )}

            {isWebpage ? (
              <Button
                variant="text"
                startIcon={<LanguageIcon />}
                endIcon={<KeyboardArrowDownIcon />}
                sx={{ color: 'white', height: '100%' }}
                onClick={(event) => setAnchorElUser(event.currentTarget)}
              >
                語言 / Language
              </Button>
            ) : (
              <IconButton
                sx={{ color: 'white', height: '100%' }}
                onClick={(event) => setAnchorElUser(event.currentTarget)}
              >
                <LanguageIcon />
              </IconButton>
            )}

            <Menu
              sx={{ mt: '45px' }}
              id="menu-appbar"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              {allLang.map((option) => (
                <MenuItem
                  divider={option.value !== 'english'}
                  selected={option.value === currentLang.value}
                  key={option.value}
                  onClick={() => languageHandler(option)}
                  sx={{ minWidth: 150, '& p': { textAlign: 'center' } }}
                >
                  <LangText textAlign="left">{option.label}</LangText>
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};
export default MainHeader;
